import { safeImageUrl } from "./url-sanitizer";

// APPS-5374 (widget twin of APPS-5135): Kalshi auto-assigns generic artwork
// from two places, and both must render as the branded Predictor tile, so
// they are treated the same as a missing image.
//
// 1. The structured-icon set (the "4 diamonds" image and its siblings:
//    calendar, temperature, price, votes, hashtag, ...) lives in a dedicated
//    S3 bucket. Matching on the bucket name rather than one FQDN also covers
//    the regional (`kalshi-fallback-images.s3.us-east-1.amazonaws.com/...`)
//    and path-style (`s3.amazonaws.com/kalshi-fallback-images/...`) URL
//    shapes S3 serves for the same objects.
// 2. The match-tie icon is generic auto-assigned artwork too (the UEFA tie
//    graphic shows up on cricket draw markets), but Kalshi serves it as
//    `assets.kalshi.com/structured_targets/*_TIE.webp`. Only that suffix is
//    gated: the rest of `structured_targets/` is genuine team and entity
//    artwork that must keep rendering.
//
// Twin of `apps/web/src/utils/market-image.ts`; keep the two in sync.
const kalshiFallbackImageBucket = "kalshi-fallback-images";
const kalshiAssetsHost = "assets.kalshi.com";
const kalshiTieIconPath = /^\/structured_targets\/[^/]+_TIE\.webp$/i;

/** The three URL shapes S3 serves for the fallback bucket's objects. */
function isKalshiFallbackBucketUrl(url: URL): boolean {
  const { host, pathname } = url;
  if (
    host.startsWith(`${kalshiFallbackImageBucket}.s3.`) &&
    host.endsWith(".amazonaws.com")
  ) {
    return true;
  }
  const isS3PathStyleHost =
    host === "s3.amazonaws.com" ||
    (host.startsWith("s3.") && host.endsWith(".amazonaws.com"));
  return (
    isS3PathStyleHost && pathname.startsWith(`/${kalshiFallbackImageBucket}/`)
  );
}

/**
 * Resolve a market / option image URL for rendering.
 *
 * Returns the safe, normalized URL when it is genuine artwork, or `null` when
 * it is absent, unsafe (see {@link safeImageUrl}), or one of Kalshi's own
 * fallback images. Callers render `<ImageFallback />` for `null`.
 *
 * Runs on top of `safeImageUrl`, so only absolute http(s) URLs reach the host
 * check; the widget never renders relative URLs, so there is no "let the
 * onError path judge" branch like the web app has.
 */
export function resolveMarketImageUrl(
  raw: string | null | undefined,
): string | null {
  const safe = safeImageUrl(raw);
  if (!safe) return null;
  // `safe` is `URL.href`, so it re-parses without a try/catch.
  const url = new URL(safe);
  if (isKalshiFallbackBucketUrl(url)) return null;
  if (url.host === kalshiAssetsHost && kalshiTieIconPath.test(url.pathname)) {
    return null;
  }
  return safe;
}
